/* =========================================
   HERO SCENE
   An isometric block with an animated conveyor belt feeding through
   it, drawn as a pure inline SVG (vector = razor-sharp at any size).
   Shaded as if lit from directly above: top faces stay the page
   colour, vertical faces fall into warm grey. After exiting the
   block, the belt runs on and off the viewport's left edge.

   Composed in paint order — layering is what sells the illusion:
     1. BeltStrip      page-fixed belt ribbon (behind block)
     2. Block          block + slot (one rigid group; slides along the
                       belt on scroll)
     3. OutgoingBelt   the run past the block,
                       cut at the block's face by the moving slot clip

   The run's end is layout-independent: on mount (and on resize) we
   measure the rendered svg and rebuild the geometry so the reference
   point turnZ sits exactly FOLD_LEFT_PX from the left edge of the
   viewport on every screen size (the paper continues well past it).

   Geometry lives in ./geometry and the scroll behaviour in
   ./use-scroll-travel.

   The svg overflows its container on purpose: the belt runs across
   and off the page. The .hero wrapper clips only horizontally, so the
   off-screen belt ends can't cause page scrollbars.
   ========================================= */
'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  OUT_CLIP,
  DEFAULT_TURN_Z,
  FOLD_LEFT_PX,
  WAVE_LEN,
  WAVE_SPEED,
  makeBeltPaths,
  makeTurnGeometry,
  turnZForFoldScreenX,
} from './geometry'
import { MARK_FLOW_SPEED, buildRunMarks, markPath } from './marks'
import { useScrollTravel } from './use-scroll-travel'
import { BeltStrip, OutgoingBelt, type WaveRefs } from './belt'
import { Block } from './block'

export function HeroScene() {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [turn, setTurn] = useState(() => makeTurnGeometry(DEFAULT_TURN_Z))
  const { blockRef, clipRef, travelRef } = useScrollTravel(turn.maxTravel)

  /* The faux print: blurred mark layout for the incoming paper
     (the printed stretch ends at the block, so it's turn-independent). */
  const runMarks = useMemo(() => buildRunMarks(), [])

  /* The travelling wave: every frame, advance the ripple's phase and
     rewrite the four wavy surfaces directly on the DOM (no React
     re-render), then re-place every mark onto the new surface at its
     advanced flow position — the ink rides the paper. Crests and
     print flow with the belt, top-right → bottom-left; the flat
     zone through the block follows the current travel. */
  const waveRefs = useRef<WaveRefs>({
    inTop: { current: null },
    inSide: { current: null },
    outTop: { current: null },
    outSide: { current: null },
    marks: { current: null },
  }).current
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let raf = 0
    const t0 = performance.now()
    const tick = (now: number) => {
      const elapsed = (now - t0) / 1000
      const phase = (elapsed * WAVE_SPEED) % WAVE_LEN
      const travel = travelRef.current
      const p = makeBeltPaths(turn.turnZ, phase, travel)
      waveRefs.inTop.current?.setAttribute('d', p.inTop)
      waveRefs.inSide.current?.setAttribute('d', p.inSide)
      waveRefs.outTop.current?.setAttribute('d', p.outTop)
      waveRefs.outSide.current?.setAttribute('d', p.outSide)

      const flow = elapsed * MARK_FLOW_SPEED
      const marksEl = waveRefs.marks.current
      if (marksEl) {
        const kids = marksEl.children
        const n = Math.min(kids.length, runMarks.marks.length)
        for (let i = 0; i < n; i++) {
          kids[i].setAttribute(
            'd',
            markPath(runMarks.marks[i], turn.turnZ, runMarks.laneLen, phase, travel, flow)
          )
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [turn.turnZ, waveRefs, runMarks])

  // Pin the fold FOLD_LEFT_PX from the viewport's left edge,
  // re-solving whenever the layout changes size.
  useLayoutEffect(() => {
    const measure = () => {
      const el = svgRef.current
      if (!el) return
      const turnZ = turnZForFoldScreenX(
        el.getBoundingClientRect(),
        FOLD_LEFT_PX
      )
      setTurn((prev) =>
        Math.abs(turnZ - prev.turnZ) < 0.01 ? prev : makeTurnGeometry(turnZ)
      )
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return (
    <div className="hero-scene" aria-hidden="true">
      <svg
        ref={svgRef}
        viewBox="0 0 500 500"
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
        style={{ overflow: 'visible' }}
        fill="none"
        stroke="#111111"
        strokeWidth={1.15}
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        <defs>
          {/* Moving: hugs the slot, translated with the block */}
          <clipPath id="belt-out-clip">
            <path ref={clipRef} d={OUT_CLIP} />
          </clipPath>
          {/* The whole scene (paper AND block) dissolves toward the
              run's bottom-left end. The gradient runs along the belt
              axis; the mask region is set explicitly and generously —
              the scene extends far outside the 500x500 viewBox. */}
          <linearGradient
            id="scene-end-fade-grad"
            gradientUnits="userSpaceOnUse"
            x1={turn.endFade.x1}
            y1={turn.endFade.y1}
            x2={turn.endFade.x2}
            y2={turn.endFade.y2}
          >
            <stop offset="0" stopColor="#fff" />
            <stop offset="1" stopColor="#000" stopOpacity="0" />
          </linearGradient>
          <mask
            id="scene-end-fade"
            maskUnits="userSpaceOnUse"
            x="-6000"
            y="-2000"
            width="12000"
            height="6000"
          >
            <rect
              x="-6000"
              y="-2000"
              width="12000"
              height="6000"
              fill="url(#scene-end-fade-grad)"
            />
          </mask>
        </defs>

        <g mask="url(#scene-end-fade)">
          <BeltStrip turn={turn} waveRefs={waveRefs} runMarks={runMarks} />
          <Block groupRef={blockRef} />
          <OutgoingBelt turn={turn} waveRefs={waveRefs} />
        </g>
      </svg>
    </div>
  )
}
